import { Component, computed, effect, input, OnDestroy, output, signal } from '@angular/core';
export type KlAlertType = 'success' | 'error' | 'info' | 'warning';

/**
 * Inline alert (ported from legacy `kl-alert`): icon strip, message(s), optional close, optional auto-dismiss.
 */
@Component({
  selector: 'kl-alert',
  standalone: false,
  templateUrl: './kl-alert.html',
  styleUrl: './kl-alert.scss',
})
export class KlAlertComponent implements OnDestroy {
  readonly alertHeader = input<string | undefined>();
  readonly message = input<string | string[]>('');
  readonly type = input<KlAlertType | string>('error');
  readonly show = input(false);
  readonly showClose = input(true);
  readonly autoClose = input(false);
  readonly loading = input(false);

  readonly onClose = output<void>();

  readonly multiMessageExpanded = signal(false);

  readonly messageIsString = computed(() => typeof this.message() === 'string');
  readonly stringMessage = computed(() => (this.messageIsString() ? (this.message() as string) : ''));
  readonly arrayMessage = computed(() => (!this.messageIsString() ? (this.message() as string[]) : []));

  private autoCloseTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    effect(() => {
      const show = this.show();
      const autoClose = this.autoClose();
      // Track message to reset expanded state on change
      this.message();

      this.clearAutoCloseTimer();
      this.multiMessageExpanded.set(false);

      if (show && autoClose) {
        this.autoCloseTimer = setTimeout(() => this.dismiss(), 10_000);
      }
    });
  }

  ngOnDestroy(): void {
    this.clearAutoCloseTimer();
  }

  iconClass(): string {
    if (this.loading()) {
      return 'ph ph-spinner kl-alert__icon-spin';
    }
    switch (this.type()) {
      case 'success':
        return 'ph ph-check-circle';
      case 'error':
        return 'ph ph-x-circle';
      case 'info':
        return 'ph ph-info';
      case 'warning':
      case 'warn':
        return 'ph ph-warning';
      default:
        return 'ph ph-info';
    }
  }

  dismiss(): void {
    this.clearAutoCloseTimer();
    this.onClose.emit();
  }

  toggleMultiMessage(): void {
    if (this.messageIsString()) return;
    this.multiMessageExpanded.update((v) => !v);
  }

  private clearAutoCloseTimer(): void {
    if (this.autoCloseTimer !== undefined) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = undefined;
    }
  }
}
